import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, MapPin, MoreHorizontal } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { formatDistanceToNow } from 'date-fns'
import PostActions from '../components/PostActions'
import VideoPlayer from '../components/VideoPlayer'
import PostHeader from '../components/PostHeader'
import Comment, { CommentType } from '../components/Comment'
import Avatar from '../components/ui/Avatar'
import ShareModal from '../components/ShareModal'
import MapModal from '../components/MapModal'
import PostOptionsDropdown from '../components/PostOptionsDropdown'
import QuoteRepostModal from '../components/QuoteRepostModal'
import { useMediaPreview } from '../contexts/MediaPreviewContext'
import { useSocialInteractions } from '../hooks/useSocialInteractions'
import api from '../services/api'

export default function PostDetail() {
  const { postId } = useParams()
  const navigate = useNavigate()
  const { openPreview } = useMediaPreview()
  const [showShareModal, setShowShareModal] = useState(false)
  const [showMapModal, setShowMapModal] = useState(false)
  const [showQuoteModal, setShowQuoteModal] = useState(false)
  const [commentText, setCommentText] = useState('')
  const [showOptionsMenu, setShowOptionsMenu] = useState(false)
  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | undefined>(undefined)
  const moreButtonRef = useRef<HTMLButtonElement>(null)
  const [post, setPost] = useState<any>(null)
  const [comments, setComments] = useState<CommentType[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  // Social interactions — same hook as feed
  const socialInteractions = useSocialInteractions(postId || '')
  const {
    liked: isLiked,
    saved: isBookmarked,
    reposted: isReposted,
    likesCount,
    commentsCount,
    repostsCount,
    loading: socialLoading,
    actions: { toggleLike, toggleSave, toggleRepost, sharePost }
  } = socialInteractions

  // Get current user from localStorage
  const currentUser = (() => {
    try {
      const userData = localStorage.getItem('user')
      if (userData) return JSON.parse(userData)
      const profile = localStorage.getItem('user_profile')
      if (profile) return JSON.parse(profile)
      return null
    } catch { return null }
  })()
  const currentUserId = currentUser?.userId || currentUser?.id || null

  const isOwner = currentUserId && post?.authorId === currentUserId

  useEffect(() => {
    if (postId) {
      loadPost()
      loadComments()
    }
  }, [postId])

  const loadPost = async () => {
    try {
      setLoading(true)
      // Fetch from posts-api
      const response = await fetch(`https://api.onrecord.live/posts/${postId}`)
      if (response.ok) {
        const data = await response.json()
        if (data.post) {
          setPost({
            id: data.post.id || data.post.post_id || postId,
            authorId: data.post.author?.id || data.post.user_id || data.post.authorId,
            author: {
              name: data.post.author?.name || data.post.authorName || 'User',
              username: data.post.author?.username || data.post.authorUsername || 'user',
              avatar: data.post.author?.avatar || data.post.authorAvatar,
              verified: data.post.author?.verified || false,
            },
            content: data.post.content || data.post.text || '',
            mediaUrl: data.post.media?.url || data.post.media_url || data.post.mediaUri || data.post.mediaUrl,
            mediaType: data.post.media?.type || data.post.media_type || data.post.mediaType || 'image',
            createdAt: data.post.created_at || data.post.createdAt || new Date().toISOString(),
            location: data.post.location ? {
              ...data.post.location,
              address: data.post.location.city
                ? data.post.location.state
                  ? `${data.post.location.city}, ${data.post.location.state}`
                  : data.post.location.city
                : data.post.location.address || undefined,
              latitude: data.post.location.latitude || data.post.latitude,
              longitude: data.post.location.longitude || data.post.longitude,
            } : null,
            stats: {
              likes: data.post.likes_count || data.post.stats?.likes || 0,
              comments: data.post.comments_count || data.post.stats?.comments || 0,
              reposts: data.post.reposts_count || data.post.stats?.reposts || 0,
              views: data.post.views_count || data.post.stats?.views || 0,
            },
            onRecordCode: data.post.onrecord_code || data.post.post_code || data.post.onRecordCode || data.post.postCode,
            authenticityScore: data.post.authenticity?.score || data.post.authenticityScore || 100,
            authenticityLevel: data.post.authenticity?.level || data.post.authenticityLevel || 'VERIFIED',
          })
        }
      }
    } catch (error) {
      console.error('Error loading post:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadComments = async () => {
    try {
      const result = await api.getComments(postId!)
      if (result.success && result.comments) {
        setComments(result.comments.map((c: any) => ({
          id: c.id || c.comment_id,
          author: {
            name: c.author?.name || c.authorName || 'User',
            username: c.author?.username || c.authorUsername || 'user',
            avatar: c.author?.avatar,
            verified: c.author?.verified || false,
          },
          content: c.content || c.text || '',
          createdAt: c.created_at || c.createdAt || new Date().toISOString(),
          likes: c.likes_count || 0,
          replies: c.replies || [],
        })))
      }
    } catch (error) {
      console.error('Error loading comments:', error)
    }
  }

  const handlePostComment = async () => {
    if (!commentText.trim() || submitting) return
    setSubmitting(true)
    try {
      const result = await api.addComment(postId!, commentText.trim())
      if (result.success) {
        setCommentText('')
        await loadComments() // Refresh comments
        // Update comment count
        setPost((p: any) => p ? {
          ...p,
          stats: { ...p.stats, comments: (p.stats.comments || 0) + 1 }
        } : p)
      }
    } catch (error) {
      console.error('Error posting comment:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const handleReply = (commentId: string) => {
    console.log('Reply to comment:', commentId)
  }

  const handleLikeComment = (commentId: string) => {
    console.log('Like comment:', commentId)
  }

  const handleMoreClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (moreButtonRef.current) {
      const rect = moreButtonRef.current.getBoundingClientRect()
      setMenuPosition({ x: rect.left, y: rect.bottom })
    }
    setShowOptionsMenu(true)
  }

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-onrecord-orange" />
      </div>
    )
  }

  if (!post) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center">
        <h2 className="text-xl font-bold mb-2">Post not found</h2>
        <p className="text-gray-500 mb-4">This post may have been deleted.</p>
        <button onClick={() => navigate(-1)} className="text-onrecord-orange hover:underline">Go back</button>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-700 hover:bg-gray-100 rounded-full p-2"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold">Post</h1>
          </div>
          <button
            ref={moreButtonRef}
            onClick={handleMoreClick}
            className="text-gray-500 hover:text-onrecord-orange rounded-full p-2 hover:bg-orange-50"
          >
            <MoreHorizontal className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Post */}
      <div className="border-b border-gray-200 p-4">
        <PostHeader
          author={post.author}
          onRecordCode={post.onRecordCode}
          avatarSize="lg"
          onUserClick={() => post.authorId && navigate(`/profile/${post.authorId}`)}
        />

        <div className="mt-3">
          <p className="text-gray-900 text-lg whitespace-pre-wrap">{post.content}</p>
        </div>

        {post.location?.address && (
          <button
            onClick={() => setShowMapModal(true)}
            className="mt-2 flex items-center gap-1 text-gray-400 hover:text-onrecord-orange transition-colors"
          >
            <MapPin className="w-3.5 h-3.5" />
            <span className="text-sm">{post.location.address}</span>
          </button>
        )}

        {post.mediaUrl && (
          <div
            className="mt-3 inline-block rounded-2xl overflow-hidden border border-gray-200 cursor-pointer"
            style={{ maxHeight: '510px', maxWidth: '100%' }}
            onClick={() => openPreview({
              mediaUrl: post.mediaUrl,
              mediaType: post.mediaType,
              post: {
                id: post.id,
                authorId: post.authorId,
                author: post.author,
                content: post.content,
                stats: post.stats,
                onRecordCode: post.onRecordCode,
                location: post.location,
              },
            })}
          >
            {post.mediaType === 'video' ? (
              <VideoPlayer
                src={post.mediaUrl}
                autoPlay
                controls
                className="block"
                style={{ maxHeight: '510px', maxWidth: '100%' }}
              />
            ) : (
              <img
                src={post.mediaUrl}
                alt="Post media"
                className="block"
                style={{ maxHeight: '510px', maxWidth: '100%' }}
              />
            )}
          </div>
        )}

        {/* Timestamp + Views — like Twitter */}
        <div className="mt-3 text-gray-500 text-sm">
          {new Date(post.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
          {' · '}
          {new Date(post.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          {(post.stats.views || 0) > 0 && (
            <>
              {' · '}
              <span className="font-bold text-gray-900">
                {post.stats.views >= 1000 ? `${(post.stats.views / 1000).toFixed(1)}K` : post.stats.views}
              </span>
              {' Views'}
            </>
          )}
        </div>

        {/* Action bar — identical to feed posts */}
        <div className="border-t border-b border-gray-200 mt-2">
          <PostActions
            stats={{
              likes: likesCount || post.stats?.likes || 0,
              comments: commentsCount || post.stats?.comments || 0,
              reposts: repostsCount || post.stats?.reposts || 0,
              views: post.stats?.views || 0,
            }}
            onLike={toggleLike}
            onComment={() => {
              const input = document.querySelector('input[placeholder="Post your reply"]') as HTMLInputElement
              if (input) input.focus()
            }}
            onRepost={toggleRepost}
            onQuote={() => setShowQuoteModal(true)}
            onShare={sharePost}
            onBookmark={toggleSave}
            isLiked={isLiked}
            isBookmarked={isBookmarked}
            isReposted={isReposted}
            loading={socialLoading}
            hasMedia={!!post.mediaUrl}
            onRecordCode={post.onRecordCode}
            location={post.location}
            timeAgo={formatDistanceToNow(new Date(post.createdAt), { addSuffix: true })}
            capturedAt={post.capturedAt}
            createdAt={post.createdAt}
            className="mt-0"
          />
        </div>
      </div>

      {/* Compact comment input — Twitter style */}
      <div className="px-4 py-3 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <Avatar src={currentUser?.avatar} alt={currentUser?.name || 'You'} size="sm" />
          <input
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Post your reply"
            className="flex-1 text-sm outline-none text-gray-700 placeholder-gray-400"
            onKeyDown={(e) => { if (e.key === 'Enter' && commentText.trim()) handlePostComment() }}
          />
          <button
            onClick={handlePostComment}
            disabled={!commentText.trim() || submitting}
            className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${
              commentText.trim()
                ? 'bg-onrecord-orange text-white hover:bg-onrecord-orange-dark'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {submitting ? '...' : 'Reply'}
          </button>
        </div>
      </div>

      {/* Comments */}
      <div className="divide-y divide-gray-200">
        {comments.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>No comments yet</p>
            <p className="text-sm mt-1">Be the first to comment!</p>
          </div>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="px-4">
              <Comment
                comment={comment}
                onReply={handleReply}
                onLike={handleLikeComment}
              />
            </div>
          ))
        )}
      </div>

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        post={{ id: post.id, author: post.author, content: post.content, mediaUrl: post.mediaUrl }}
      />

      <QuoteRepostModal
        isOpen={showQuoteModal}
        onClose={() => setShowQuoteModal(false)}
        post={{
          id: post.id,
          author: post.author,
          content: post.content,
          mediaUrl: post.mediaUrl,
          mediaType: post.mediaType,
          onRecordCode: post.onRecordCode,
        }}
      />

      {post.location && (
        <MapModal
          isOpen={showMapModal}
          onClose={() => setShowMapModal(false)}
          location={post.location}
          postInfo={{
            onRecordCode: post.onRecordCode,
            timestamp: post.createdAt,
          }}
        />
      )}

      <PostOptionsDropdown
        isOpen={showOptionsMenu}
        onClose={() => setShowOptionsMenu(false)}
        isOwner={isOwner}
        post={{ id: post.id, authorId: post.authorId, content: post.content, author: post.author }}
        buttonPosition={menuPosition}
      />
    </div>
  )
}
